// src/modules/app-views/invoices/pages/Init/index.jsx
// *******************************************************************************
// INVOICES LIST PAGE
// *******************************************************************************
// Main listing page for fiscal invoices (NFe, NFCe, NFSe).
// Displays a table with all invoices and allows filtering by status and type.
// *******************************************************************************

// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useEffect, useCallback, useMemo, useRef, useState } from 'react';
import { Card, Table, Tooltip, Button, Space, Dropdown } from 'antd';
import { BiExpandAlt, BiCollapseAlt } from 'react-icons/bi';
import ActionsButton from 'layouts/Pages/PageHeader/ActionsButton';
import {
  FileTextOutlined,
  CloudUploadOutlined,
  PrinterOutlined,
  EditOutlined,
  EyeOutlined,
  MoreOutlined,
  ContainerOutlined,
  RestOutlined,
  FileProtectOutlined,
} from '@ant-design/icons';
import { PiLifebuoy } from 'react-icons/pi';
import { useListContext } from 'components/core/ListProvider';
import { useModuleContext } from 'components/core/ModuleProvider';
import useZustand from 'hooks/useZustand';
import translateX from 'utils/translateX';
import invoicesService from 'modules/app-views/invoices/service';
import appService from 'services/appService';
import InvoiceTypesTag from 'modules/app-views/invoices/components/InvoiceTypesTag';
import showPreviewNFCeModal from './modals/PreviewNFCeModal';
import showEmitInvoiceNfceModal from './modals/EmitInvoiceNfceModal';
import { isMobileView, returnOnlyNumbers } from 'utils/helpers';
import FiscalBootstrapModal, { needsFiscalBootstrap } from './modals/FiscalBootstrapModal';
import '../../components/invoices-list.less';
import {
  documentTypeColumn,
  invoiceStatusColumn,
  documentNumberColumn,
  orderNumberColumn,
  operationNatureColumn,
  invoiceOperationTypeColumn,
  recipientColumn,
  issueDateColumn,
  itemsCountColumn,
  totalAmountColumn,
} from './components/tableColumns';
import {
  itemLineNumberColumn,
  itemValidationColumn,
  itemSkuColumn,
  itemDescriptionColumn,
  itemUnitColumn,
  itemQuantityColumn,
  itemUnitPriceColumn,
  itemDiscountColumn,
  itemTotalColumn,
  itemNcmColumn,
  itemCfopColumn,
  itemCestColumn,
  itemIbptColumn,
  itemIcmsColumn,
  itemIcmsStColumn,
  itemIpiColumn,
  itemPisColumn,
  itemCofinsColumn,
} from './components/tableColumns/items';

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const InvoicesInit = () => {
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  const {
    data,
    loading,
    tableKey,
    buildTableColumns,
    availableTableColumns,
    buildNumberLineColumn,
    buildCreatedAtColumn,
    buildUpdatedAtColumn,
    buildDocumentInfoIconsColumn,
    buildActionsButtonColumn,
    buildColumnProps,
    handleTableChanges: handleTableChangesCtx,
    pagination,
    rowSelection,
    tableComponents,
    hiddenColumns,
    isEditingTable,
    setIsEditingTable,
    query,
    refresh,
    registerFloatButtonDisabledActions,
  } = useListContext();

  const {
    handleEditDocument,
    handleArchiveDocuments,
    handleRemoveDocuments,
    handleRestoreDocuments,
    handlePreviewDocument,
  } = useModuleContext();

  const storedLanguage = useZustand('theme', 'language');
  const storedListSettings = useZustand('module', 'listSettings');
  const storedCompany = useZustand('auth', 'company');
  const storedSubscription = useZustand('auth', 'subscription');

  // Current folder
  const folder = storedListSettings?.filterDrawer?.folder || query?.drawer?.folder || 'documents';

  // Serialize sort, search, filter, and pagination for stable dependencies
  const sortKey = JSON.stringify(query?.sort || []);
  const searchKey = JSON.stringify(query?.search || {});
  const filterKey = JSON.stringify(query?.filter || {});
  const paginationKey = pagination?.current || 1;

  // 2. HOOKS & REFS
  const toolbarRef = useRef(null);

  // 3. LOCAL VARIABLES I

  // 4. LOCAL STATE
  // Fullscreen toggle for the invoices list card
  const [isFullscreen, setIsFullscreen] = useState(false);

  // Toolbar height tracked for fullscreen scroll.y calculation
  const [toolbarH, setToolbarH] = useState(49);

  // Fiscal bootstrap modal state - shows when company needs fiscal configuration
  const [showFiscalBootstrap, setShowFiscalBootstrap] = useState(() => {
    return needsFiscalBootstrap(storedCompany, storedSubscription);
  });

  // Track which invoice is currently being emitted (to disable button)
  const [emittingInvoiceId, setEmittingInvoiceId] = useState(null);

  // Track expanded rows (subtables) - controlled to reset on sort/filter/search/pagination
  const [expandedRowKeys, setExpandedRowKeys] = useState([]);

  // 5. REFS & HANDLERS (using refs to maintain stable function identity)

  const handleToggleFullscreen = useCallback(() => {
    setIsFullscreen(prev => !prev);
    appService.message({
      flag: 'i',
      text: !isFullscreen ? 'fullscreen_enabled' : 'fullscreen_disabled',
    });
  }, [isFullscreen]);

  // Store handlers in refs to avoid re-creating render functions
  const handlersRef = useRef({});
  handlersRef.current = {
    handleEditDocument,
    handleArchiveDocuments,
    handleRemoveDocuments,
    handleRestoreDocuments,
    handlePreviewDocument,
    refresh,
    folder,
    emittingInvoiceId,
    setEmittingInvoiceId,
  };

  // Stable render function for DOCUMENTS folder (doesn't change identity)
  const renderDocumentsActions = useCallback((_, record) => {
    const {
      handleEditDocument,
      handleArchiveDocuments,
      handleRemoveDocuments,
      handlePreviewDocument,
      refresh,
      emittingInvoiceId,
      setEmittingInvoiceId,
    } = handlersRef.current;

    const status = record?.status;
    const isAuthorized = status === 'authorized';
    const isDraft = status === 'draft';
    const isRejected = status === 'rejected';
    const isError = status === 'error';
    const isProcessedBySefaz = invoicesService.isProcessedBySefaz(status);

    // Check if document is validated
    const isValidated = record?.documentValidation?.isValid === true;

    // Check if document can be emitted (not processed and is validated)
    const canEmit = (isDraft || isRejected || isError) && isValidated;

    // Check if document needs validation (not processed, not validated, or has error)
    const needsValidation = (isDraft || isRejected || isError) && !isValidated;

    // Options with onSuccess callback to refresh list after action
    const actionOptions = { onSuccess: refresh };

    // Handle view/print preview
    const handleViewPreview = () => showPreviewNFCeModal({ data: record });

    // Handle validate invoice
    const handleValidateInvoice = async () => {
      const invoiceId = record?._id;
      if (!invoiceId) return;

      const result = await invoicesService.validate(invoiceId);
      if (result) {
        refresh?.();
      }
    };

    // Check if this invoice is currently being emitted
    const isEmitting = emittingInvoiceId === record?._id;

    // Handle emit invoice
    // 1. First validates if the issuer company is eligible to emit
    // 2. If recipient is generic person (no CPF/CNPJ), open modal to ask for CPF
    // 3. Otherwise, emit directly without modal
    const handleEmitInvoice = async () => {
      // Prevent double-click
      if (emittingInvoiceId) return;

      // Helper to show/hide loading message and manage emitting state
      const showLoading = () => {
        setEmittingInvoiceId(record?._id);
        appService.message({
          flag: 'loading',
          text: 'emitting_invoice',
          key: 'emit-invoice-loading',
          duration: 0, // Keep visible until manually hidden
        });
      };
      const hideLoading = () => {
        setEmittingInvoiceId(null);
        appService.message({
          flag: 'loading',
          key: 'emit-invoice-loading',
          duration: 0.1, // Close immediately
        });
      };

      // Step 1: Validate if company is eligible to emit this document type
      const validation = await invoicesService.validateEmissionEligibility({
        invoice: record,
      });

      if (!validation.valid) {
        // Notification already shown by validateEmissionEligibility
        return;
      }

      // Step 2: Check recipient document
      const recipient = record?.recipient || {};
      const isGenericPerson = recipient?.isGenericPerson || recipient?.isGenericPeople;
      // Check cpfCnpj first (used for generic consumers), then cpf
      const hasCpf = returnOnlyNumbers(
        recipient?.cpfCnpj || recipient?.cpf?.value || recipient?.cpf
      );
      const hasCnpj = returnOnlyNumbers(recipient?.cnpj?.value || recipient?.cnpj);
      const hasDocument = hasCpf || hasCnpj;

      // If generic person but has CPF (filled in RecipientCard), skip modal and use that CPF
      if (isGenericPerson && hasCpf && hasCpf.length === 11) {
        // Show loading immediately - direct emission without modal
        showLoading();

        // Emit directly with the existing CPF
        try {
          const invoiceId = record?._id;
          const documentType = record?.documentType?.toUpperCase() || 'NFCE';

          const result = await invoicesService.emit(invoiceId, documentType, {
            cpf: hasCpf,
          });

          hideLoading();

          if (result?.success) {
            appService.message({
              flag: 'success',
              text: 'invoice_emitted_successfully',
            });
            refresh?.();
          } else {
            const errorMessage = result?.message || result?.error || 'emit_invoice_error';
            appService.notification({
              flag: 'error',
              title: 'emit_invoice_error',
              description: errorMessage,
              noTranslateDescription: true,
            });
            refresh?.();
          }
        } catch (error) {
          hideLoading();
          appService.notification({
            flag: 'error',
            title: 'emit_invoice_error',
            description: error?.message || 'unknown_error',
            noTranslateDescription: true,
          });
          refresh?.();
        }
        return;
      }

      // NFe does not require CPF - emit directly regardless of recipient document
      const documentType = record?.documentType;
      if (documentType === 'nfe') {
        showLoading();
        try {
          const result = await invoicesService.emit(record?._id, 'NFE', {});
          hideLoading();
          if (result?.success) {
            appService.message({ flag: 'success', text: 'invoice_emitted_successfully' });
            refresh?.();
          } else {
            appService.notification({
              flag: 'error',
              title: 'emit_invoice_error',
              description: result?.message || result?.error || 'emit_invoice_error',
              noTranslateDescription: true,
            });
            refresh?.();
          }
        } catch (error) {
          hideLoading();
          appService.notification({
            flag: 'error',
            title: 'emit_invoice_error',
            description: error?.message || 'unknown_error',
            noTranslateDescription: true,
          });
        }
        return;
      }

      // If generic person without CPF or no document, open modal to ask for CPF
      // Modal handles its own loading state
      if (isGenericPerson || !hasDocument) {
        showEmitInvoiceNfceModal({
          invoiceData: record,
          onSuccess: refresh,
        });
        return;
      }

      // Step 3: If has document (CNPJ), emit directly without modal
      // Show loading immediately - direct emission without modal
      showLoading();

      try {
        const invoiceId = record?._id;
        const documentType = record?.documentType?.toUpperCase() || 'NFCE';

        const result = await invoicesService.emit(invoiceId, documentType, {
          cpf: hasCpf || null,
        });

        hideLoading();

        if (result?.success) {
          appService.message({
            flag: 'success',
            text: 'invoice_emitted_successfully',
          });
          refresh?.();
        } else {
          const errorMessage = result?.message || result?.error || 'emit_invoice_error';
          appService.notification({
            flag: 'error',
            title: 'emit_invoice_error',
            description: errorMessage,
            noTranslateDescription: true,
          });
          refresh?.();
        }
      } catch (error) {
        hideLoading();
        appService.notification({
          flag: 'error',
          title: 'emit_invoice_error',
          description: error?.message || 'unknown_error',
          noTranslateDescription: true,
        });
      }
    };

    // Build all action buttons in priority order (left to right)
    // Button config: { key, icon, label, onClick, disabled?, danger? }
    const allButtons = [];

    // 1. Primary action: Print (authorized), Emit (validated), Validate (needs validation)
    if (isAuthorized) {
      allButtons.push({
        key: 'print',
        icon: <PrinterOutlined />,
        label: translateX('print'),
        onClick: handleViewPreview,
      });
    } else if (canEmit) {
      allButtons.push({
        key: 'emit',
        icon: <CloudUploadOutlined />,
        label: translateX('emit_invoice'),
        onClick: handleEmitInvoice,
        disabled: isEmitting,
        loading: isEmitting,
      });
    } else if (needsValidation) {
      allButtons.push({
        key: 'validate',
        icon: <FileProtectOutlined />,
        label: translateX('validate_invoice'),
        onClick: handleValidateInvoice,
      });
    } else {
      // For other statuses (pending, canceled, denied), show disabled emit button
      allButtons.push({
        key: 'emit',
        icon: <CloudUploadOutlined />,
        label: translateX('emit_invoice'),
        onClick: handleEmitInvoice,
        disabled: true,
      });
    }

    // 2. View (preview drawer)
    allButtons.push({
      key: 'view',
      icon: <EyeOutlined />,
      label: translateX('view'),
      onClick: () => handlePreviewDocument?.(record),
    });

    // 3. Edit
    allButtons.push({
      key: 'edit',
      icon: <EditOutlined />,
      label: translateX('edit'),
      onClick: () => handleEditDocument?.(record),
    });

    // 4. Archive
    allButtons.push({
      key: 'archive',
      icon: <ContainerOutlined />,
      label: translateX('archive'),
      onClick: () => handleArchiveDocuments?.([record], actionOptions),
      disabled: isProcessedBySefaz,
    });

    // 5. Remove
    allButtons.push({
      key: 'remove',
      icon: <RestOutlined />,
      label: translateX('remove'),
      onClick: () => handleRemoveDocuments?.([record], actionOptions),
      danger: true,
      disabled: isProcessedBySefaz,
    });

    return renderActionButtons(allButtons);
  }, []); // Empty deps - uses ref for latest values

  // Stable render function for ARCHIVE and RECYCLE folders
  const renderArchiveRecycleActions = useCallback((_, record) => {
    const { handleRestoreDocuments, handleRemoveDocuments, refresh, folder } = handlersRef.current;
    const isRecycle = folder === 'recycle';
    const actionOptions = { onSuccess: refresh };

    // Build all action buttons in priority order
    const allButtons = [
      {
        key: 'restore',
        icon: <PiLifebuoy />,
        label: translateX('restore'),
        onClick: () => handleRestoreDocuments?.([record], actionOptions),
      },
      {
        key: 'remove',
        icon: <RestOutlined />,
        label: isRecycle ? translateX('permanently_remove') : translateX('remove'),
        onClick: () => handleRemoveDocuments?.([record], actionOptions),
        danger: isRecycle,
      },
    ];

    return renderActionButtons(allButtons);
  }, []); // Empty deps - uses ref for latest values

  // Expanded row render - shows invoice items in a nested table
  const expandedRowRender = useCallback(record => {
    const items = record?.items || [];

    if (items.length === 0) {
      return (
        <div className="invoices-list-card__empty-items">
          {translateX('no_items_added')}
        </div>
      );
    }

    // Columns for the items subtable (built from extracted column functions)
    const itemColumns = [
      itemLineNumberColumn(),
      itemValidationColumn(),
      itemSkuColumn(),
      itemDescriptionColumn(),
      itemUnitColumn(),
      itemQuantityColumn(),
      itemUnitPriceColumn(),
      itemDiscountColumn(),
      itemTotalColumn(),
      itemNcmColumn(),
      itemCfopColumn(),
      itemCestColumn(),
      itemIbptColumn(),
      itemIcmsColumn(),
      itemIcmsStColumn(),
      itemIpiColumn(),
      itemPisColumn(),
      itemCofinsColumn(),
    ];

    // Add unique keys to items that don't have _id
    const itemsWithKeys = items.map((item, idx) => ({
      ...item,
      _rowKey: item?._id || item?.product?._id || `item-${record._id}-${idx}`,
    }));

    return (
      <Table
        columns={itemColumns}
        dataSource={itemsWithKeys}
        rowKey="_rowKey"
        pagination={false}
        size="small"
        bordered={false}
        showHeader={true}
        scroll={{ x: 'max-content' }}
        className="nested-items-table"
      />
    );
  }, []);

  // 6. EFFECTS

  // Track toolbar height so fullscreen wrapper max-height fills remaining viewport
  useEffect(() => {
    const el = toolbarRef.current;
    if (!el) return;
    const ro = new ResizeObserver(entries => {
      const h = entries[0].borderBoxSize?.[0]?.blockSize ?? entries[0].contentRect.height;
      setToolbarH(Math.ceil(h) + 1); // +1 for border-bottom
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Reset expanded rows (close all subtables) when sort/search/filter/pagination changes
  // This prevents showing stale subtable data from a previous row position
  useEffect(() => {
    setExpandedRowKeys([]);
  }, [sortKey, searchKey, filterKey, paginationKey]);

  // Check if fiscal bootstrap modal needs to be shown when company/subscription changes
  useEffect(() => {
    const needsBootstrap = needsFiscalBootstrap(storedCompany, storedSubscription);
    setShowFiscalBootstrap(needsBootstrap);
  }, [storedCompany, storedSubscription]);

  // Register disabled actions for float button bar
  // Disables archive/remove when ANY selected document is processed by SEFAZ
  useEffect(() => {
    registerFloatButtonDisabledActions?.((selectedRows, actionKey) => {
      // Only check for archive and remove actions
      if (!['archive', 'remove'].includes(actionKey)) return false;

      // Check if at least one selected row is processed by SEFAZ
      return selectedRows.some(row => invoicesService.isProcessedBySefaz(row?.status));
    });

    return () => {
      // Cleanup on unmount
      registerFloatButtonDisabledActions?.(null);
    };
  }, [registerFloatButtonDisabledActions]);

  useEffect(() => {
    buildTableColumns([
      // Line number
      buildNumberLineColumn(),

      // TIPO - Document type (NFe, NFCe, NFSe)
      // Filter only shown if subscription has both NFe and NFCe resources
      // Uses InvoiceTypesTag for the render (invoices-specific)
      documentTypeColumn(buildColumnProps, {
        filter: false,
        renderTag: record => <InvoiceTypesTag data={record} />,
      }),

      // STATUS - InvoiceStatusTag + DocumentValidationTag
      invoiceStatusColumn(buildColumnProps),

      // NOTA - documentSerie + documentNumber (ex: 1-000000123)
      documentNumberColumn(buildColumnProps, { search: false }),

      // PEDIDO - orderNumber
      orderNumberColumn(buildColumnProps, { search: false }),

      // NATUREZA - operationNature (translated)
      operationNatureColumn(buildColumnProps),

      // OPERATION - operationType (exit/entry)
      invoiceOperationTypeColumn(buildColumnProps),

      // CLIENTE - recipient.name with subtitle [cpfCnpj | LC: xxx]
      recipientColumn(buildColumnProps),

      // ISSUE DATE - issueDate
      issueDateColumn(buildColumnProps),

      // ITENS - items count
      itemsCountColumn(buildColumnProps),

      // TOTAL - totals.documentAmount
      totalAmountColumn(buildColumnProps),

      // Created at
      buildCreatedAtColumn(),

      // Updated at
      buildUpdatedAtColumn(),

      // Icons Document Info
      buildDocumentInfoIconsColumn({}),

      // Actions - Custom render based on folder
      buildActionsButtonColumn({
        view: true,
        customRender: ['archive', 'recycle'].includes(folder)
          ? renderArchiveRecycleActions
          : renderDocumentsActions,
      }),
    ]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    storedListSettings?.showNumberOfLine,
    sortKey,
    searchKey,
    filterKey,
    storedLanguage,
    hiddenColumns,
    isEditingTable,
    query?.drawer?.folder,
    folder,
    emittingInvoiceId, // Re-render when emitting state changes to update button disabled/loading
    // Note: renderDocumentsActions and renderArchiveRecycleActions are NOT included
    // as dependencies to avoid infinite loops. They use refs/closures for latest values.
  ]);

  // 7. UI
  const cardStyle = isFullscreen
    ? {
        position: 'fixed',
        inset: 0,
        zIndex: 1000,
        borderRadius: 0,
        boxShadow: 'none',
        overflow: 'hidden',
        transition: 'all 0.3s ease',
      }
    : { transition: 'all 0.3s ease' };

  const tableScroll = useMemo(() => ({ x: 'max-content' }), []);

  return (
    <>
      {isFullscreen && (
        <style>{`
          .ant-layout-content { z-index: 1000 !important; }
          #server-wrapper-content { z-index: 1000 !important; }
        `}</style>
      )}
      <Card
        variant="outlined"
        className="invoices-list-card"
        styles={{ body: { padding: 0 } }}
        style={cardStyle}
      >
        <div className="invoices-list-card__toolbar" ref={toolbarRef}>
          <div className="invoices-list-card__toolbar-actions">
            <ActionsButton
              locate="pageHeader"
              affixWhen="none"
              appendButtons={
                <Tooltip title={translateX(isFullscreen ? 'restore_window' : 'expand_window')}>
                  <Button
                    type="default"
                    onClick={handleToggleFullscreen}
                    icon={isFullscreen ? <BiCollapseAlt /> : <BiExpandAlt />}
                  />
                </Tooltip>
              }
            />
          </div>
        </div>

        <div
          className={
            isFullscreen ? 'invoices-list-card__table-scroll allow-inner-scroll' : undefined
          }
          style={
            isFullscreen
              ? { overflow: 'auto', maxHeight: `calc(100vh - ${toolbarH}px)` }
              : undefined
          }
        >
        <Table
          key={tableKey}
          rowKey="_id"
          bordered
          columns={availableTableColumns}
          dataSource={data}
          loading={loading}
          onChange={handleTableChangesCtx}
          pagination={pagination}
          rowSelection={
            !isEditingTable && hiddenColumns?.includes('__selection__')
              ? undefined
              : isEditingTable
                ? { ...rowSelection, getCheckboxProps: () => ({ disabled: true }) }
                : rowSelection
          }
          scroll={tableScroll}
          size="middle"
          className="with-checkbox"
          components={tableComponents}
          expandable={{
            columnTitle: (
              <Tooltip title={translateX('customize_table')}>
                <span
                  onClick={e => {
                    e.stopPropagation();
                    setIsEditingTable(!isEditingTable);
                  }}
                  className="invoices-list-card__expand-btn"
                >
                  <span
                    className={`invoices-list-card__edit-dot${isEditingTable ? ' active' : ''}`}
                  />
                </span>
              </Tooltip>
            ),
            expandedRowRender,
            rowExpandable: record => (record?.items?.length || 0) > 0,
            expandedRowKeys,
            onExpandedRowsChange: setExpandedRowKeys,
          }}
          locale={{
            emptyText: (
              <div className="invoices-list-card__empty-text">
                <FileTextOutlined className="invoices-list-card__empty-text-icon" />
                <div>{translateX('no_invoices_found')}</div>
              </div>
            ),
          }}
        />
        </div>
      </Card>

      {/* Fiscal Bootstrap Modal */}
      <FiscalBootstrapModal
        show={showFiscalBootstrap}
        setShow={setShowFiscalBootstrap}
        company={storedCompany}
        subscription={storedSubscription}
        afterClose={() => {
          refresh?.();
        }}
      />
    </>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default InvoicesInit;

// *******************************************************************************
// HELPERS
// *******************************************************************************

/**
 * Renders action buttons with responsive behavior
 * - Desktop: up to 4 visible buttons (if more, show 3 + MORE)
 * - Mobile: 1 visible if only 1 button, otherwise all in MORE
 * - MORE button disabled if all menu items are disabled
 */
function renderActionButtons(buttons) {
  if (!buttons || buttons.length === 0) return null;

  const isMobile = isMobileView();

  // Mobile: 1 visible if only 1, otherwise all in MORE
  if (isMobile) {
    if (buttons.length === 1) {
      const btn = buttons[0];
      return (
        <Tooltip title={btn.label}>
          <Button
            size="small"
            type="text"
            icon={btn.icon}
            onClick={btn.onClick}
            disabled={btn.disabled}
            danger={btn.danger}
            loading={btn.loading}
          />
        </Tooltip>
      );
    }

    // Multiple buttons on mobile: all in MORE dropdown
    const allDisabled = buttons.every(btn => btn.disabled);
    const menuItems = buttons.map(btn => ({
      key: btn.key,
      icon: btn.icon,
      label: btn.label,
      danger: btn.danger,
      disabled: btn.disabled,
      onClick: btn.onClick,
    }));

    return (
      <Dropdown menu={{ items: menuItems }} trigger={['click']} disabled={allDisabled}>
        <Tooltip title={translateX('more')}>
          <Button size="small" type="text" icon={<MoreOutlined />} disabled={allDisabled} />
        </Tooltip>
      </Dropdown>
    );
  }

  // Desktop: up to 4 visible (if more, show 3 + MORE)
  if (buttons.length <= 4) {
    return (
      <Space size={4}>
        {buttons.map(btn => (
          <Tooltip key={btn.key} title={btn.label}>
            <Button
              size="small"
              type="text"
              icon={btn.icon}
              onClick={btn.onClick}
              disabled={btn.disabled}
              danger={btn.danger}
              loading={btn.loading}
            />
          </Tooltip>
        ))}
      </Space>
    );
  }

  // More than 4 buttons: show first 3 + MORE
  const visibleButtons = buttons.slice(0, 3);
  const menuButtons = buttons.slice(3);
  const allMenuDisabled = menuButtons.every(btn => btn.disabled);

  const menuItems = menuButtons.map(btn => ({
    key: btn.key,
    icon: btn.icon,
    label: btn.label,
    danger: btn.danger,
    disabled: btn.disabled,
    onClick: btn.onClick,
  }));

  return (
    <Space size={4}>
      {visibleButtons.map(btn => (
        <Tooltip key={btn.key} title={btn.label}>
          <Button
            size="small"
            type="text"
            icon={btn.icon}
            onClick={btn.onClick}
            disabled={btn.disabled}
            danger={btn.danger}
            loading={btn.loading}
          />
        </Tooltip>
      ))}
      <Dropdown menu={{ items: menuItems }} trigger={['click']} disabled={allMenuDisabled}>
        <Tooltip title={translateX('more')}>
          <Button size="small" type="text" icon={<MoreOutlined />} disabled={allMenuDisabled} />
        </Tooltip>
      </Dropdown>
    </Space>
  );
}
